import { Avatar, Button, Divider, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { BluePenEdit, WatchFile } from 'Images';
import { IFile, getFileName } from 'API';
import { deleteFile, postFile, getFile } from 'API';
import { useEffect, useRef, useState } from 'react';
import { checkFileSize, isFileValid } from 'Utils';
import { FILE_ERROR_TYPES } from 'Enum/FileErrorTypes';
import { Loader } from 'Components/Loader';
import { useLoader } from 'Hooks/useLoader';
import {
  POLLING_INTERVAL,
  POLLING_INTERVAL_FIRST_MESSAGE,
} from 'Modules/DocumentsHandle/FilesSection';

interface ActionsProps {
  file: IFile;
  onFileUpdate: () => void;
}

export const Actions = ({ file, onFileUpdate }: ActionsProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.DOCUMENTS_HANDLE);
  const isDesktop = useIsDesktop();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number | null>(null);
  const [isPressed, setIsPressed] = useState(false);
  const [showProcessingMessage, setShowProcessingMessage] = useState(false);
  const { isLoading, loader, setIsLoading } = useLoader();

  const checkExtension = (file: File) => {
    const allowsExtension = ['jpg', 'png', 'pdf'];
    const fileExt = file.name.split('.').pop();
    if (fileExt && allowsExtension.includes(fileExt.toLowerCase())) return true;
    setErrorType(FILE_ERROR_TYPES.FILE_TYPE_INVALID);
    return false;
  };

  const handleReplaceFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setErrorType(null);
    const newFile = event.target.files?.[0];

    if (!newFile) return;
    if (!checkFileSize(newFile)) {
      setErrorType(FILE_ERROR_TYPES.FILE_TOO_BIG);
      setShowError(true);
      return;
    }
    if (!checkExtension(newFile)) {
      setShowError(true);
      return;
    }
    if (!(await isFileValid(newFile))) {
      setShowError(true);
      return;
    }

    try {
      setIsLoading(true);

      const fileWithoutExtension = newFile.name.split('.');
      const fileNTimestamp =
        fileWithoutExtension[0].replace(/[^a-zA-Z0-9\u05D0-\u05EA\- _]/g, '') +
        '-' +
        Date.now();
      const fileExt = newFile.name.split('.').pop();
      const fileName = fileNTimestamp + '.' + fileExt;

      const form = new FormData();
      form.append('file', newFile, fileName);

      const fileData = await postFile(
        file.fileCategoryRelationId,
        form,
        file.caseDetailsRelationId,
        file.witnessId,
        file.hospitalizationDetailsRelationId
      );

      if (fileData) {
        await deleteFile(file.id);
        setShowError(false);
        setIsPressed(true);

        const intervalId = setInterval(async () => {
          const updatedFileData = await getFileName(fileData.id);

          if (updatedFileData && updatedFileData.isAnalysisOccured) {
            clearInterval(intervalId);
            setIsLoading(false);
            onFileUpdate();
          }
        }, POLLING_INTERVAL);
      } else {
        setShowError(true);
        setIsLoading(false);
      }
    } catch (error) {
      setShowError(true);
      setIsLoading(false);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setShowProcessingMessage(true);
      }, POLLING_INTERVAL_FIRST_MESSAGE);

      return () => clearTimeout(timer);
    } else {
      setShowProcessingMessage(false);
    }
  }, [isLoading]);

  const handleViewFile = async () => {
    const fileData = await getFile(file.id, file.userGivenName);
    if (fileData) {
      window.open(URL.createObjectURL(fileData), file.userGivenName);
    }
  };

  return isLoading ? (
    <Stack width={'400px'} alignItems='center'>
      <Loader width={'40px'} />
      {showProcessingMessage && (
        <Typography fontSize='14px !important'>
          {dictionary.processingMayTakeTime}
        </Typography>
      )}
    </Stack>
  ) : (
    <Stack
      direction='row'
      alignItems='center'
      gap={isDesktop ? 0 : '12px'}
      sx={{
        width: 'fit-content',
        whiteSpace: 'nowrap',
      }}
    >
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleReplaceFile}
        accept='.pdf,.jpg,.png'
      />
      <Button
        variant='text'
        onClick={() => fileInputRef.current?.click()}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Avatar
          alt=''
          src={BluePenEdit}
          sx={{ width: '18px', height: '18px' }}
        />
        <Typography
          color='#1752AF'
          fontSize={isDesktop ? '18px !important' : '16px !important'}
        >
          {dictionary.changeFile}
        </Typography>
      </Button>
      <Divider orientation='vertical' sx={{ height: '24px' }} />

      <Button
        variant='text'
        onClick={handleViewFile}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Avatar alt='' src={WatchFile} sx={{ width: '24px', height: '24px' }} />
        <Typography
          color='#1752AF'
          fontSize={isDesktop ? '18px !important' : '16px !important'}
        >
          {dictionary.watch}
        </Typography>
      </Button>
    </Stack>
  );
};
